import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fetchAllProducts } from "@/lib/api";

const StoreContext = createContext(null);

export function StoreProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const pendingAction = useRef(null);

  const [cartData, setCartData] = useState({ cart: null, totals: null });
  const [wishlist, setWishlist] = useState({ ids: [], products: [] });
  const [pincodeInfo, setPincodeInfo] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sy_pincode") || "null"); } catch { return null; }
  });
  const [drawer, setDrawer] = useState({ open: false, tab: "bag" });
  const [searchOpen, setSearchOpen] = useState(false);
  const [catalog, setCatalog] = useState({ categories: [], brands: [] });

  const isAuthed = !!customer;

  const loadCart = useCallback(async () => {
    try {
      const pin = JSON.parse(localStorage.getItem("sy_pincode") || "null")?.pincode;
      const { data } = await api.get("/ecom/cart", { params: { ...(pin && { pincode: pin }) } });
      setCartData(data);
    } catch { /* guest or expired */ }
  }, []);

  const loadWishlist = useCallback(async () => {
    try {
      const { data } = await api.get("/ecom/wishlist");
      setWishlist({ ids: data.product_ids || [], products: data.products || [] });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    api.get("/ecom/catalog/categories?include_counts=true").then((r) =>
      setCatalog((c) => ({ ...c, categories: r.data || [] }))).catch(() => {});
    api.get("/ecom/catalog/brands?include_counts=true").then((r) =>
      setCatalog((c) => ({ ...c, brands: r.data || [] }))).catch(() => {});
      // warm the full-catalog pool so homepage sections paint instantly
      fetchAllProducts().catch(() => {});
  }, []);

  useEffect(() => {
    const boot = async () => {
      const ts = Number(localStorage.getItem("sy_token_at") || 0);
      if (ts && Date.now() - ts > 24 * 60 * 60 * 1000) {
        localStorage.removeItem("sy_token");
        localStorage.removeItem("sy_token_at");
      }
      if (localStorage.getItem("sy_token")) {
        try {
          const { data } = await api.get("/ecom/auth/me");
          setCustomer(data);
          await Promise.all([loadCart(), loadWishlist()]);
        } catch { /* interceptor wiped token */ }
      }
      setAuthReady(true);
    };
    boot();
  }, [loadCart, loadWishlist]);

  useEffect(() => {
    const onUnauthorized = () => {
      setCustomer(null);
      setCartData({ cart: null, totals: null });
      setWishlist({ ids: [], products: [] });
      setLoginOpen(true);
    };
    window.addEventListener("sy:unauthorized", onUnauthorized);
    return () => window.removeEventListener("sy:unauthorized", onUnauthorized);
  }, []);

  const requestOtp = async (mobile, purpose = "login") => {
    const { data } = await api.post("/ecom/auth/otp/request", { mobile, purpose });
    return data;
  };

  const verifyOtp = async (mobile, otp) => {
    const { data } = await api.post("/ecom/auth/otp/verify", { mobile, otp });
    localStorage.setItem("sy_token", data.token);
    localStorage.setItem("sy_token_at", String(Date.now()));
    setCustomer(data.customer);
    await Promise.all([loadCart(), loadWishlist()]);
    return data;
  };

  const finishLogin = (cust) => {
    setLoginOpen(false);
    toast.success(`Welcome${cust?.name ? ` ${cust.name}` : ""} to Soonyoung`);
    const action = pendingAction.current;
    pendingAction.current = null;
    if (action) action();
  };

  useEffect(() => {
    // recompute store-specific nudges when the delivery pincode changes
    if (pincodeInfo?.pincode && localStorage.getItem("sy_token")) loadCart();
  }, [pincodeInfo?.pincode, loadCart]);

  const refreshMe = async () => {
    const { data } = await api.get("/ecom/auth/me");
    setCustomer(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/ecom/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("sy_token");
    setCustomer(null);
    setCartData({ cart: null, totals: null });
    setWishlist({ ids: [], products: [] });
    toast.success("Signed out — come back soon");
  };

  const requireAuth = useCallback((action) => {
    if (localStorage.getItem("sy_token")) action();
    else {
      pendingAction.current = action;
      setLoginOpen(true);
    }
  }, []);

  // Cart mutations return the same {cart, totals} payload — update the nudge from every response
  const applyCartResponse = async (data) => {
    if (data?.totals && data?.cart) setCartData(data);
    else await loadCart();
  };

  const addToCart = async (productId, qty = 1) => {
    try {
      const { data } = await api.post("/ecom/cart/items", { product_id: productId, qty });
      if (data?.notice) toast.info(data.notice);
      await applyCartResponse(data);
      toast.success("Added to your bag", { action: { label: "View", onClick: () => setDrawer({ open: true, tab: "bag" }) } });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const updateQty = async (itemId, qty) => {
    try {
      const { data } = await api.put(`/ecom/cart/items/${itemId}`, { qty });
      if (data?.notice) toast.info(data.notice);
      await applyCartResponse(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeCartItem = async (itemId) => {
    try {
      const { data } = await api.delete(`/ecom/cart/items/${itemId}`);
      if (data?.notice) toast.info(data.notice);
      await applyCartResponse(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const applyCoupon = async (code) => {
    try {
      const { data } = await api.post("/ecom/cart/coupon/apply", { code });
      setCartData({ cart: data.cart, totals: data.totals });
      toast.success(`Coupon ${code} applied — saved ${data?.totals?.coupon_discount ?? ""}`);
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };

  const removeCoupon = async () => {
    try {
      const { data } = await api.delete("/ecom/cart/coupon");
      if (data?.totals && data?.cart) setCartData(data);
      else await loadCart();
      toast.success("Coupon removed");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleWishlist = async (productId) => {
    try {
      const { data } = await api.post(`/ecom/wishlist/${productId}`);
      await loadWishlist();
      toast.success(data.wished ? "Saved to your rituals" : "Removed from saved rituals");
      return data.wished;
    } catch (e) { toast.error(errMsg(e)); }
  };

  const checkPincode = async (pincode) => {
    const { data } = await api.get(`/ecom/catalog/store-for-pincode/${pincode}`);
    const info = { pincode, ...data };
    setPincodeInfo(info);
    localStorage.setItem("sy_pincode", JSON.stringify(info));
    return info;
  };

  const cartCount = cartData?.totals?.item_count || 0;

  const value = useMemo(() => ({
    customer, isAuthed, authReady, loginOpen, setLoginOpen, requestOtp, verifyOtp, finishLogin, refreshMe, logout, requireAuth,
    cartData, cartCount, loadCart, addToCart, updateQty, removeCartItem, applyCoupon, removeCoupon,
    wishlist, toggleWishlist,
    pincodeInfo, checkPincode,
    drawer, openDrawer: (tab = "bag") => setDrawer({ open: true, tab }), closeDrawer: () => setDrawer((d) => ({ ...d, open: false })),
    setDrawerTab: (tab) => setDrawer((d) => ({ ...d, tab })),
    searchOpen, setSearchOpen,
    catalog,
  }), [customer, isAuthed, authReady, loginOpen, cartData, cartCount, wishlist, pincodeInfo, drawer, searchOpen, catalog, requireAuth, loadCart]);

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export const useStore = () => useContext(StoreContext);
